import * as Sentry from '@sentry/react';
import { deleteToken, getMessaging } from 'firebase/messaging';
import { useChatUserStore, useNotificationsPermissionsStore } from '../store';
import { IDENTITY_SIGN_OUT_URL, redirectToSignOut, userManager } from './oidc';
import { wait } from './wait';
import { useDeleteDeviceToken } from '../pages/Messenger/queries';

// A slow or failing cleanup request must not block signing out.
const DEVICE_CLEANUP_TIMEOUT_MS = 3000;

const deleteFcmToken = async () => {
	try {
		await deleteToken(getMessaging());
	} catch (error) {
		console.log(error, 'deletion of token failed');
	}
};

// oidc-react keeps its user in state after removeUser(), so a router
// navigation would still render the app as signed in. A full load through the
// identity server's logout ends its session too, then comes back to sign in.
const signOutLocally = async () => {
	try {
		await userManager.removeUser();
	} catch (error) {
		console.log(error, 'local sign out failed');
	}
	window.location.replace(IDENTITY_SIGN_OUT_URL);
};

export const useSignOut = () => {
	const { setChatUser } = useChatUserStore();
	const { token } = useNotificationsPermissionsStore();
	const deleteDeviceTokenMutation = useDeleteDeviceToken();

	const onSignOut = async () => {
		setChatUser(null);
		localStorage.removeItem('afterLoginRedirect');

		// Runs before the redirect, while the access token is still stored.
		await Promise.race([
			Promise.allSettled([
				deleteFcmToken(),
				token
					? deleteDeviceTokenMutation.mutateAsync(token)
					: Promise.resolve(),
			]),
			wait(DEVICE_CLEANUP_TIMEOUT_MS),
		]);

		try {
			await redirectToSignOut();
		} catch (error) {
			Sentry.captureException(error, {
				tags: { section: 'oidc-auth-sign-out' },
			});
			await signOutLocally();
		}
	};

	return onSignOut;
};
